import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { QuoteModal } from "@/components/QuoteModal";
import { useState } from "react";

const TermsConditions = () => {
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 animate-fade-in">Terms & Conditions</h1>
          <p className="text-muted-foreground mb-8">Effective Date: January 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">1. Agreement to Terms</h2>
              <p className="text-muted-foreground mb-4">
                These Terms and Conditions ("Terms") govern your use of Costal Meridian's shipping and logistics services. By engaging our services, you agree to be bound by these Terms.
              </p>
              <p className="text-muted-foreground">
                Costal Meridian operates under United States law and international shipping conventions, including the Hague-Visby Rules for sea freight and the Montreal Convention for air freight.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">2. Services</h2>
              <p className="text-muted-foreground mb-4">Costal Meridian provides the following services:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Ocean freight (FCL and LCL)</li>
                <li>Air freight and express delivery</li>
                <li>Intermodal and multimodal transport</li>
                <li>Customs brokerage and documentation</li>
                <li>Warehousing and distribution</li>
                <li>Project cargo and heavy lift</li>
                <li>Cargo insurance</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">3. Booking and Quotations</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Quotations are valid for 14 days unless otherwise stated</li>
                <li>Prices are subject to currency fluctuations and fuel surcharges</li>
                <li>Bookings are confirmed upon receipt of written acceptance and deposit (where applicable)</li>
                <li>Cargo details must be accurate; discrepancies may result in additional charges</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">4. Customer Responsibilities</h2>
              <p className="text-muted-foreground mb-4">The customer agrees to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide accurate cargo descriptions, weights, and dimensions</li>
                <li>Ensure proper packaging suitable for the mode of transport</li>
                <li>Provide all necessary documentation for customs clearance</li>
                <li>Comply with all applicable laws and regulations</li>
                <li>Not ship prohibited or restricted goods without proper authorization</li>
                <li>Declare dangerous goods according to IMDG/IATA regulations</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">5. Liability and Limitations</h2>
              <h3 className="text-xl font-semibold mb-3">Ocean Freight</h3>
              <p className="text-muted-foreground mb-4">
                Liability is limited to 2 SDR per kilogram or 666.67 SDR per package, whichever is higher, as per the Hague-Visby Rules.
              </p>
              <h3 className="text-xl font-semibold mb-3">Air Freight</h3>
              <p className="text-muted-foreground mb-4">
                Liability is limited to 22 SDR per kilogram as per the Montreal Convention.
              </p>
              <h3 className="text-xl font-semibold mb-3">Road Transport</h3>
              <p className="text-muted-foreground mb-4">
                Liability is limited to 8.33 SDR per kilogram as per CMR Convention.
              </p>
              <p className="text-muted-foreground">
                We strongly recommend cargo insurance for full value protection.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">6. Payment Terms</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Payment is due within 30 days of invoice date unless otherwise agreed</li>
                <li>Late payments incur interest at 1.5% per month</li>
                <li>Costal Meridian reserves a lien over goods for unpaid charges</li>
                <li>All prices are quoted in USD unless specified otherwise</li>
                <li>Credit accounts are subject to approval</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">7. Claims</h2>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Claims for visible damage must be made at time of delivery</li>
                <li>Claims for concealed damage must be made within 3 days of delivery</li>
                <li>Written notice of claims must be submitted within 21 days</li>
                <li>Claims are time-barred after 1 year from delivery date</li>
                <li>All claims must be supported by documentation</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">8. Force Majeure</h2>
              <p className="text-muted-foreground">
                Costal Meridian shall not be liable for delays or failure to perform due to circumstances beyond our reasonable control, including but not limited to: natural disasters, war, terrorism, strikes, port congestion, government actions, pandemics, or extreme weather conditions.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">9. Prohibited Goods</h2>
              <p className="text-muted-foreground mb-4">The following items are prohibited:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Illegal substances and narcotics</li>
                <li>Weapons and ammunition (without proper licensing)</li>
                <li>Counterfeit goods</li>
                <li>Live animals (without proper authorization)</li>
                <li>Items prohibited by international conventions</li>
              </ul>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">10. Governing Law</h2>
              <p className="text-muted-foreground mb-4">
                These Terms are governed by the laws of the State of California, USA. Any disputes shall be submitted to the exclusive jurisdiction of the courts of Orange County, California, USA.
              </p>
              <p className="text-muted-foreground">
                For international shipments, the applicable international conventions shall take precedence where mandatory.
              </p>
            </section>

            <section className="animate-fade-in">
              <h2 className="text-2xl font-bold mb-4">11. Contact Information</h2>
              <div className="bg-secondary p-6 rounded-lg">
                <p className="text-muted-foreground">
                  <strong>Costal Meridian Legal Department</strong><br />
                  555 Anton Blvd suite 1050<br />
                  Costa Mesa, CA 92626, USA<br />
                  Email: support@costalmeridian.com<br />
                  Phone: (714) 798-3650
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default TermsConditions;
